import { Trans, withI18n } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { PureComponent } from 'react'
import { connectRefinementList } from 'react-instantsearch-dom'

import { ABO_LABEL } from 'shared/consts'
import {
  FILTER_FLYOUT_CLOSE,
  FILTER_SEARCH_OPTIONS,
  FILTERS_DEBOUNCE_TIME,
  PRODUCT_FILTER_CHANGED_SFX,
  PRODUCT_FILTER_RESET_SFX,
} from 'shared/experiments/components/NewSerp/consts'
import FlyoutFilter from 'shared/experiments/components/NewSerp/FlyoutFilter/FlyoutFilter'
import { withSearchStateHandler } from 'shared/experiments/components/NewSerp/helpers'
import ListItem from 'shared/experiments/components/NewSerp/RefinementList/ListItem'
import type {
  RefinementListChangeAction,
  RefinementListCloseAction,
  RefItem,
} from 'shared/experiments/components/NewSerp/types'
import { RefinementListItemType } from 'shared/experiments/components/NewSerp/types'
import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import store, { subscribe } from 'shared/store'
import type { InferPropsWithDefaults } from 'shared/utils/propTypes'
import type { Experiment } from 'types/shopConfig'
import IconBin from 'views/components/atoms/Icon/IconBin'
import IconSearch from 'views/components/atoms/Icon/IconSearch'
import Input from 'views/components/atoms/Input'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import debounce from 'views/utils/debounce'
import { publicRuntimeConfigType } from 'views/utils/propTypes'
import { i18nType } from 'views/utils/propTypes/i18nType'

import './RefinementList.scss'

class RefinementList extends PureComponent<ComponentProps, ComponentState> {
  subscriptions: CallableFunction[] = []
  state = this.getInitialState()
  searchForItems: CallableFunction

  // There are many instances of the component on the page,
  // so to react only to the adjusted filter, individual events are introduced
  eventNameChange = `${PRODUCT_FILTER_CHANGED_SFX}${this.props.attribute}`
  eventNameReset = `${PRODUCT_FILTER_RESET_SFX}${this.props.attribute}`

  constructor(props: ComponentProps) {
    super(props)

    this.bindSubscriptions()
    this.searchForItems = debounce(
      this.searchForItemsWorker,
      FILTERS_DEBOUNCE_TIME
    )
  }

  getInitialState(): ComponentState {
    return {
      optionsSearchUsed: false,
      searchItem: '',
      ...this.getDerivedState(),
    }
  }

  componentDidUpdate(prevProps: Readonly<ComponentProps>) {
    if (prevProps.items !== this.props.items) {
      this.setState(this.getDerivedState())
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(unsub => unsub())
  }

  getDerivedState(): ComponentState {
    const { items, attribute, searchStateRefinementList } = this.props
    const { searchItem } = this.state || {}

    let itemsFinal = items.filter(
      item => item?.label !== ABO_LABEL
    ) as RefItem[]
    //@ts-ignore
    const selectedValues = searchStateRefinementList?.[attribute] || []

    if (searchItem) {
      itemsFinal = this.filterOptions(searchItem, itemsFinal)
    }

    return { items: itemsFinal, selectedValues }
  }

  bindSubscriptions() {
    this.subscriptions.push(
      subscribe.after(
        this.eventNameChange,
        (payload: RefinementListChangeAction) => {
          this.setState(
            prevState => {
              const { selectedValues } = prevState
              const { selected, isChecked } = payload
              const uniqueValues = new Set(selectedValues)

              uniqueValues[isChecked ? 'add' : 'delete'](selected)

              return {
                selectedValues: Array.from(uniqueValues),
              }
            },
            () =>
              withSearchStateHandler(() =>
                this.props.refine(this.state.selectedValues)
              )
          )
        }
      )
    )
    this.subscriptions.push(
      subscribe.after(
        FILTER_FLYOUT_CLOSE,
        (payload: RefinementListCloseAction) => {
          if (payload.attribute === this.props.attribute) {
            this.setState(
              {
                selectedValues: this.props.currentRefinement as [],
                searchItem: '',
              },
              this.searchCategoryOption
            )
          }
        }
      )
    )
  }

  filterOptions = (searchItem: string, items: RefItem[]): RefItem[] => {
    const needle = searchItem
      ?.toLowerCase()
      .trim()
      .replace(/\s{2,}/g, ' ')

    return (
      needle
        ? items.filter(
            ({ isRefined, label }) =>
              isRefined || label.toLowerCase().includes(needle)
          )
        : items
    ).filter(item => item.label !== ABO_LABEL)
  }

  /**
   * Component uses debounced call via `this.searchForItems`
   */
  searchForItemsWorker = () => {
    this.setState((prevState, prevProps) => {
      return {
        items: this.filterOptions(
          prevState.searchItem as string,
          prevProps.items as RefItem[]
        ),
      }
    })
  }

  /**
   * Options search input should be displayed even in case when there are
   * less options than FILTER_SEARCH_OPTIONS, to avoid of input field flickering
   */
  shouldDisplaySearchInput = (items: unknown[]): boolean => {
    const { optionsSearchUsed } = this.state

    return Boolean(
      this.props.search &&
        (optionsSearchUsed ||
          (!optionsSearchUsed && items.length >= FILTER_SEARCH_OPTIONS))
    )
  }

  searchCategoryOption = () => {
    this.searchForItems(this.state.searchItem || '')
  }

  handleCategoriesSearch = (event: Event) => {
    event.preventDefault()

    const target = event.target as HTMLInputElement
    this.setState(
      {
        searchItem: target.value,
        optionsSearchUsed: true,
      },
      this.searchCategoryOption
    )
  }

  handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.searchCategoryOption()
    }
  }

  handleApplyClick = () =>
    withSearchStateHandler(() => {
      this.props.refine(this.state.selectedValues)
      this.setState({ searchItem: '' })
      store.dispatch({ type: FILTER_FLYOUT_CLOSE })
    })

  handleResetClick = () => {
    this.setState({ selectedValues: [], searchItem: '' }, () =>
      withSearchStateHandler(() => {
        this.props.refine([])
        this.searchCategoryOption()
      })
    )
    store.dispatch({ type: this.eventNameReset })
  }

  render() {
    const { attribute, label, i18n, currentRefinement, publicRuntimeConfig } =
      this.props
    const {
      items = [],
      selectedValues = [],
      searchItem,
      optionsSearchUsed,
    } = this.state
    const isActive = currentRefinement.length > 0
    const shouldDisableControls =
      !selectedValues.length && !searchItem && !isActive

    const isRebranding = isRebrandingITActive(
      publicRuntimeConfig.pageProperties.experiments as Experiment[]
    )

    return (
      <FlyoutFilter
        attribute={attribute}
        label={label}
        active={isActive}
        count={currentRefinement.length}
        hidden={!optionsSearchUsed && !items.length}
      >
        <div
          hidden={!this.shouldDisplaySearchInput(items)}
          className="u-margin--bottom u-padding-small u-border u-border-color--greyLight u-border-radius e-RefinementList__search-bar"
        >
          <IconSearch className="u-color--grey" />
          <Input
            className="e-RefinementList__searchOptionsInput m-FormControlWithFloatingLabel__formControl a-inputWithClear a-h3 u-font-weight--normal u-no-margin"
            name={`search-options-${attribute}`}
            value={searchItem}
            onChange={this.handleCategoriesSearch}
            onKeyUp={this.handleKeyUp}
            disableAutoCompleteAndFriends
            placeholder={i18n._('general.button.label.search')}
          />
        </div>
        <div className="e-RefinementList">
          {Boolean(items.length) && (
            <ul className="a-list-reset">
              {items.map(item => (
                <ListItem key={item.value} item={item} attribute={attribute} />
              ))}
            </ul>
          )}
        </div>
        <div className="u-margin--top u-padding--top u-border--top">
          <ButtonAsLink
            className={classNames('o-RefinementList__resetButton', {
              'u-font-weight--bold': !shouldDisableControls,
            })}
            onClick={this.handleResetClick}
            disabled={shouldDisableControls}
          >
            <TextWithIcon
              icon={<IconBin size={isRebranding ? 'small' : 'default'} />}
            >
              <Trans id="searchResults.label.clearFilter" />
            </TextWithIcon>
          </ButtonAsLink>
        </div>
      </FlyoutFilter>
    )
  }
}

const propTypes = {
  i18n: i18nType.isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  attribute: string.isRequired,
  label: string.isRequired,
  items: arrayOf(RefinementListItemType).isRequired,
  refine: func.isRequired,
  currentRefinement: arrayOf(string).isRequired,
  searchStateRefinementList: shape({}),
  maxVisible: number,
  maxCollapsed: number,
  search: bool,
  isRebranding: bool,
}
const defaultProps = {
  searchStateRefinementList: null,
  maxVisible: 10,
  maxCollapsed: 10,
  search: false,
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = Partial<{
  items: RefItem[]
  selectedValues: string[]
  optionsSearchUsed: boolean
  searchItem: string
}>

export const RefinementListNonConnected = withShopAndRuntimeConfig(
  withI18n()(RefinementList)
)
export default connectRefinementList(RefinementListNonConnected)
